import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Search, Filter, X, ChevronDown, User as UserIcon, AlertTriangle, CheckCircle, Phone, Mail, Eye } from 'lucide-react';
import MentorLayout from '../../components/mentor/MentorLayout';
import StudentProfileModal from '../../components/mentor/StudentProfileModal';
import axios from 'axios';


import { BACKEND_URL } from '../../config/api';
const StudentsList = ({ user, onLogout }) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedFeeStatus, setSelectedFeeStatus] = useState('');
  const [progressRange, setProgressRange] = useState([0, 100]);
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedBoard, setSelectedBoard] = useState('');
  const [selectedEnrollmentMonth, setSelectedEnrollmentMonth] = useState('');
  const [sortBy, setSortBy] = useState('name');
  
  // Options for dropdowns
  const [courses, setCourses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [enrollmentMonths, setEnrollmentMonths] = useState([]);

  useEffect(() => {
    fetchStudents();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [
    searchTerm, selectedCourse, selectedFeeStatus, progressRange,
    selectedSubscription, selectedSchool, selectedGrade, selectedBoard,
    selectedEnrollmentMonth, sortBy, students
  ]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(`${BACKEND_URL}/api/mentor/students/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
      setFilteredStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      const coursesRes = await axios.get(`${BACKEND_URL}/api/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...students];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(search) ||
        s.email.toLowerCase().includes(search) ||
        (s.phone_number && s.phone_number.includes(search))
      );
    }

    if (selectedCourse) {
      filtered = filtered.filter(s => s.course_names.includes(selectedCourse));
    }

    if (selectedFeeStatus) {
      filtered = filtered.filter(s => s.fee_status === selectedFeeStatus);
    }

    filtered = filtered.filter(s => 
      s.progress_percentage >= progressRange[0] && 
      s.progress_percentage <= progressRange[1]
    );

    if (selectedSubscription) {
      filtered = filtered.filter(s => s.subscription_type === selectedSubscription);
    }

    if (selectedSchool) {
      filtered = filtered.filter(s => 
        s.school_name && s.school_name.toLowerCase().includes(selectedSchool.toLowerCase())
      );
    }

    if (selectedGrade) {
      filtered = filtered.filter(s => s.grade === selectedGrade);
    }

    if (selectedBoard) {
      filtered = filtered.filter(s => s.board === selectedBoard);
    }

    if (selectedEnrollmentMonth) {
      filtered = filtered.filter(s => s.enrollment_month === selectedEnrollmentMonth);
    }

    switch (sortBy) {
      case 'progress_asc':
        filtered.sort((a, b) => a.progress_percentage - b.progress_percentage);
        break;
      case 'progress_desc':
        filtered.sort((a, b) => b.progress_percentage - a.progress_percentage);
        break;
      case 'last_login':
        filtered.sort((a, b) => (b.last_login || '').localeCompare(a.last_login || ''));
        break;
      case 'fee_asc':
        filtered.sort((a, b) => a.outstanding_fee - b.outstanding_fee);
        break;
      case 'fee_desc':
        filtered.sort((a, b) => b.outstanding_fee - a.outstanding_fee);
        break;
      default:
        filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredStudents(filtered);
    
    const uniqueSchools = [...new Set(students.map(s => s.school_name).filter(Boolean))];
    const uniqueGrades = [...new Set(students.map(s => s.grade).filter(Boolean))];
    const uniqueMonths = [...new Set(students.map(s => s.enrollment_month).filter(Boolean))];
    
    setSchools(uniqueSchools);
    setGrades(uniqueGrades);
    setEnrollmentMonths(uniqueMonths.sort().reverse());
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCourse('');
    setSelectedFeeStatus('');
    setProgressRange([0, 100]);
    setSelectedSubscription('');
    setSelectedSchool('');
    setSelectedGrade('');
    setSelectedBoard('');
    setSelectedEnrollmentMonth('');
    setSortBy('name');
  };

  const getRiskBadgeColor = (badge) => {
    const colors = {
      inactive_7days: 'bg-red-100 text-red-800 border-red-300',
      zero_progress: 'bg-orange-100 text-orange-800 border-orange-300',
      failed_quizzes: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      fees_pending: 'bg-purple-100 text-purple-800 border-purple-300',
      low_engagement: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[badge] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getRiskBadgeLabel = (badge) => {
    const labels = {
      inactive_7days: 'Inactive >7d',
      zero_progress: 'No Progress',
      failed_quizzes: 'Failed Quizzes',
      fees_pending: 'Fees Due',
      low_engagement: 'Low Engagement'
    };
    return labels[badge] || badge;
  };

  if (loading) {
    return (
      <MentorLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading students...</p>
          </div>
        </div>
      </MentorLayout>
    );
  }

  return (
    <MentorLayout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Students</h1>
          <p className="text-gray-600 mt-1">{filteredStudents.length} of {students.length} students</p>
        </div>

        {/* Search and Filters Bar */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="name">Sort by Name</option>
              <option value="progress_asc">Progress: Low → High</option>
              <option value="progress_desc">Progress: High → Low</option>
              <option value="last_login">Last Login: Recent</option>
              <option value="fee_asc">Fees: Low → High</option>
              <option value="fee_desc">Fees: High → Low</option>
            </select>

            {/* Filters Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Filter className="w-5 h-5" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Course Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
                  <select
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Courses</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.title}>{course.title}</option>
                    ))}
                  </select>
                </div>

                {/* Fee Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fee Status</label>
                  <select
                    value={selectedFeeStatus}
                    onChange={(e) => setSelectedFeeStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All</option>
                    <option value="Full">Full</option>
                    <option value="Partial">Partial</option>
                  </select>
                </div>

                {/* Subscription Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subscription</label>
                  <select
                    value={selectedSubscription}
                    onChange={(e) => setSelectedSubscription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All</option>
                    <option value="1 month">1 Month</option>
                    <option value="1 year">1 Year</option>
                  </select>
                </div>

                {/* Board Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Board</label>
                  <select
                    value={selectedBoard}
                    onChange={(e) => setSelectedBoard(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All</option>
                    <option value="CBSE">CBSE</option>
                    <option value="SSC">SSC</option>
                    <option value="ICSE">ICSE</option>
                  </select>
                </div>

                {/* School Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
                  <select
                    value={selectedSchool}
                    onChange={(e) => setSelectedSchool(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Schools</option>
                    {schools.map(school => (
                      <option key={school} value={school}>{school}</option>
                    ))}
                  </select>
                </div>

                {/* Grade Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                  <select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Grades</option>
                    {grades.map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>

                {/* Enrollment Month Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enrollment Month</label>
                  <select
                    value={selectedEnrollmentMonth}
                    onChange={(e) => setSelectedEnrollmentMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Months</option>
                    {enrollmentMonths.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>

                {/* Progress Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Progress: {progressRange[0]}% - {progressRange[1]}%
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={progressRange[0]}
                      onChange={(e) => setProgressRange([parseInt(e.target.value), progressRange[1]])}
                      className="flex-1"
                    />
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={progressRange[1]}
                      onChange={(e) => setProgressRange([progressRange[0], parseInt(e.target.value)])}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Students Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStudents.map(student => (
            <Card key={student.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                {/* Header with Photo and Name */}
                <div className="flex items-start gap-4 mb-4">
                  {student.profile_photo ? (
                    <img
                      src={student.profile_photo}
                      alt={student.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <UserIcon className="w-8 h-8 text-blue-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900">{student.name}</h3>
                    <p className="text-sm text-gray-600">{student.email}</p>
                    {student.phone_number && (
                      <div className="flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3 text-gray-500" />
                        <p className="text-xs text-gray-600">{student.phone_number}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    {student.activation_status === 'Activated' ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-orange-600" />
                    )}
                  </div>
                </div>

                {/* Risk Badges */}
                {student.risk_badges.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {student.risk_badges.map((badge, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskBadgeColor(badge)}`}
                      >
                        {getRiskBadgeLabel(badge)}
                      </span>
                    ))}
                  </div>
                )}

                {/* Details Grid */}
                <div className="space-y-2 text-sm">
                  {student.parent_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Parent:</span>
                      <span className="font-medium">{student.parent_name}</span>
                    </div>
                  )}
                  
                  {student.course_names.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Course:</span>
                      <span className="font-medium truncate ml-2">{student.course_names[0]}</span>
                    </div>
                  )}

                  {student.school_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">School:</span>
                      <span className="font-medium truncate ml-2">{student.school_name}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    {student.batch && (
                      <div>
                        <span className="text-gray-600 text-xs">Batch:</span>
                        <p className="font-medium">{student.batch}</p>
                      </div>
                    )}
                    {student.grade && (
                      <div>
                        <span className="text-gray-600 text-xs">Grade:</span>
                        <p className="font-medium">{student.grade}</p>
                      </div>
                    )}
                    {student.board && (
                      <div>
                        <span className="text-gray-600 text-xs">Board:</span>
                        <p className="font-medium">{student.board}</p>
                      </div>
                    )}
                    {student.subscription_type && (
                      <div>
                        <span className="text-gray-600 text-xs">Subscription:</span>
                        <p className="font-medium">{student.subscription_type}</p>
                      </div>
                    )}
                  </div>

                  {/* Fee Status */}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <span className="text-gray-600">Fee Status:</span>
                    <div className="text-right">
                      <span className={`font-bold ${student.fee_status === 'Full' ? 'text-green-600' : 'text-orange-600'}`}>
                        {student.fee_status}
                      </span>
                      {student.outstanding_fee > 0 && (
                        <p className="text-xs text-orange-600">₹{student.outstanding_fee.toLocaleString()} due</p>
                      )}
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="pt-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-bold text-blue-600">{student.progress_percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${student.progress_percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100">
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Weekly Mins</p>
                      <p className="font-bold text-sm">{student.weekly_learning_minutes}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Streak</p>
                      <p className="font-bold text-sm">{student.daily_streak}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Quiz Score</p>
                      <p className="font-bold text-sm">{student.quiz_mastery_score}%</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Attendance</p>
                      <p className="font-bold text-sm">{student.attendance_percentage}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600">GP Rewards</p>
                      <p className="font-bold text-sm">{student.gp_balance}</p>
                    </div>
                  </div>

                  {/* Last Login */}
                  {student.last_login && (
                    <div className="text-xs text-gray-600 pt-2">
                      Last login: {new Date(student.last_login).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-2 mt-4 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => setSelectedStudentId(student.id)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View Profile
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors">
                      <Phone className="w-4 h-4" />
                      Call
                    </button>
                    <button className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors">
                      <Mail className="w-4 h-4" />
                      Message
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredStudents.length === 0 && (
          <div className="text-center py-12">
            <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">No students found</p>
            <p className="text-sm text-gray-600 mt-2">Try adjusting your filters or search term</p>
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Student Profile Modal */}
      {selectedStudentId && (
        <StudentProfileModal
          studentId={selectedStudentId}
          onClose={() => setSelectedStudentId(null)}
        />
      )}
    </MentorLayout>
  );
};

export default StudentsList;
